import { types, getSnapshot, flow, clone } from 'mobx-state-tree';
import { User, NullUser } from './User';
import { ApiStore } from './ApiStore';
import { NullSalesPerformance, SalesPerformance } from './SalesPerformance';
import { calculateUserStreaks, getStreakStatus, isUserOnActiveStreak } from '../utils/streakCalculation';

const _usersStore = types.model('UsersStore', {
    currentUser: types.optional(User, getSnapshot(NullUser)),
    companyUsers: types.optional(types.array(User), []),
    allUsers: types.optional(types.array(User), []),
})
    .views(self => ({
        get isCurrentUserExecutive() {
            const { userType } = self.currentUser;
            return userType === 'Company Admin' ||
                userType === 'Executive' ||
                userType === 'admin';
        },
        get isCurrentAdmin() {
            const { userType } = self.currentUser;
            return userType === 'admin';
        },
        get isCompanyAdmin() {
            const { userType } = self.currentUser;
            return userType === 'Company Admin';
        },
        get assessmentUsers() {
            const currentUser = self.currentUser;
            return self.companyUsers.filter(u => {
                // Executives and higher can see all users including other Executives (excluding Company Admins/admins)
                if (self.isCurrentUserExecutive) {
                    return u.userType !== 'Company Admin' && u.userType !== 'admin' && u.userId !== currentUser.userId;
                }
                // Senior Managers can see their direct Sales Managers + those managers' Sales Members + any Sales Members reporting directly to them
                if (currentUser.userType === 'Senior Manager') {
                    const directManagerIds = self.companyUsers
                        .filter(sm => sm.reportsToId === currentUser.userId && sm.userType === 'Sales Manager')
                        .map(sm => sm.userId);
                    return (directManagerIds.includes(u.reportsToId) && u.userType === 'Sales Member') ||
                           (u.reportsToId === currentUser.userId && u.userType === 'Sales Manager') ||
                           (u.reportsToId === currentUser.userId && u.userType === 'Sales Member');
                }
                // Sales Managers can see their direct reports
                return u.reportsToId === currentUser.userId && u.userType === 'Sales Member';
            });
        },
        get companyAdmins() {
            const sorted = self.companyUsers
                .filter(u => u.userType === 'Company Admin')
                .slice()
                .sort((u1, u2) => `${u1.lastName}_${u1.firstName}`.localeCompare(`${u2.lastName}_${u2.firstName}`));
            return sorted;
        },
        get executives() {
            return self.companyUsers
                .filter(u => u.userType === 'Executive')
                .sort((u1, u2) => `${u1.lastName}_${u1.firstName}`.localeCompare(`${u2.lastName}_${u2.firstName}`));
        },
        get seniorManagers() {
            return self.companyUsers
                .filter(u => u.userType === 'Senior Manager')
                .sort((u1, u2) => `${u1.lastName}_${u1.firstName}`.localeCompare(`${u2.lastName}_${u2.firstName}`));
        },
        get companyAdminsWithSalesMemberReports() {
            return self.companyUsers
                .filter(u => u.userType === 'Company Admin' && self.salesMembers.some(member => member.reportsToId === u.userId))
                .sort((u1, u2) => `${u1.lastName}_${u1.firstName}`.localeCompare(`${u2.lastName}_${u2.firstName}`));
        },
        get salesMembers() {
            return self.companyUsers
                .filter(u => u.userType === 'Sales Member')
                .sort((u1, u2) => `${u1.lastName}_${u1.firstName}`.localeCompare(`${u2.lastName}_${u2.firstName}`));
        },
        get salesManagers() {
            return self.companyUsers
                .filter(u => u.userType === 'Sales Manager')
                .sort((u1, u2) => `${u1.lastName}_${u1.firstName}`.localeCompare(`${u2.lastName}_${u2.firstName}`));
        },
        get allCompanyUsers() {
            return self.companyUsers;
        },
        getAggregateSalesPerformance(usersToAggregate) {
            const users = usersToAggregate ?? self.assessmentUsers;
            const salesPerformance = users
                .map(user => user.salesPerformance)
                .reduce((r1, r2) => {
                    return {
                        totalOpportunities: {
                            count: r1.totalOpportunities.count + (r2.totalOpportunities?.count || 0),
                            value: r1.totalOpportunities.value + (r2.totalOpportunities?.value || 0)
                        },
                        wonOpportunities: {
                            count: r1.wonOpportunities.count + (r2.wonOpportunities?.count || 0),
                            value: r1.wonOpportunities.value + (r2.wonOpportunities?.value || 0)
                        },
                        expense: {
                            salary: r1.expense.salary + (r2.expense?.salary || 0),
                            commissions: r1.expense.commissions + (r2.expense?.commissions || 0),
                            expenses: r1.expense.expenses + (r2.expense?.expenses || 0),
                            attributedExpenses: r1.expense.attributedExpenses + (r2.expense?.attributedExpenses || 0)
                        }
                    };
                },
                    getSnapshot(NullSalesPerformance));

            return SalesPerformance.create(salesPerformance);
        },
        getDirectReports(managerId) {
            return managerId ?
                self.companyUsers.filter(u => u.reportsToId === managerId)
                    .sort((u1, u2) => `${u1.lastName}_${u1.firstName}`.localeCompare(`${u2.lastName}_${u2.firstName}`))
                : [];
        },
        getSeniorManagerVisibleUsers(managerId) {
            // Returns direct Sales Managers + their Sales Members + any Sales Members reporting directly to the Senior Manager
            if (!managerId) return [];
            const directManagers = self.companyUsers.filter(u => u.reportsToId === managerId && u.userType === 'Sales Manager');
            const directMembers = self.companyUsers.filter(u => u.reportsToId === managerId && u.userType === 'Sales Member');
            const nestedMembers = [];
            directManagers.forEach(manager => {
                const members = self.companyUsers.filter(u => u.reportsToId === manager.userId && u.userType === 'Sales Member');
                nestedMembers.push(...members);
            });
            return [...directManagers, ...directMembers, ...nestedMembers]
                .sort((u1, u2) => `${u1.lastName}_${u1.firstName}`.localeCompare(`${u2.lastName}_${u2.firstName}`));
        },
        /**
         * Calculate streak data for a specific user using their assessment history
         * @param {string} userId - The user ID to calculate streaks for
         * @param {Object} assessmentsStore - The assessments store containing user assessments
         * @returns {Object} - Streak calculation results
         */
        getUserStreakData(userId, assessmentsStore, companyId) {
            if (!assessmentsStore) {
                return {
                    currentStreak: 0,
                    longestStreak: 0,
                    streakHistory: [],
                    lastSubmissionDate: null,
                    totalSubmissions: 0
                };
            }

            const userAssessments = assessmentsStore.closedAssessmentsForUser(userId, companyId);
            return calculateUserStreaks(userAssessments);
        },
        /**
         * Get current user's streak status with display information
         * @param {Object} assessmentsStore - The assessments store
         * @returns {Object} - Complete streak status for display
         */
        getCurrentUserStreakStatus(assessmentsStore, companyId) {
            return self.getUserStreakStatus(self.currentUser.userId, assessmentsStore, companyId);
        },
        /**
         * Get streak status for any user with display formatting
         * @param {string} userId - The user ID
         * @param {Object} assessmentsStore - The assessments store
         * @returns {Object} - Formatted streak status
         */
        getUserStreakStatus(userId, assessmentsStore, companyId) {
            if (!assessmentsStore) {
                return {
                    currentStreak: 0,
                    longestStreak: 0,
                    isActive: false,
                    streakLevel: 'None',
                    nextMilestone: null,
                    totalSubmissions: 0
                };
            }

            const userAssessments = assessmentsStore.closedAssessmentsForUser(userId, companyId);
            return getStreakStatus(userAssessments);
        },
        /**
         * Check if a user is currently on an active streak
         * @param {string} userId - The user ID to check
         * @param {Object} assessmentsStore - The assessments store
         * @returns {boolean} - True if user has active streak
         */
        isUserOnStreak(userId, assessmentsStore, companyId) {
            if (!assessmentsStore) return false;

            const userAssessments = assessmentsStore.closedAssessmentsForUser(userId, companyId);
            return isUserOnActiveStreak(userAssessments);
        },
        /**
         * Get streak leaderboard for all company users
         * @param {Object} assessmentsStore - The assessments store
         * @returns {Array} - Array of users with streak data sorted by current streak
         */
        getCompanyStreakLeaderboard(assessmentsStore, companyId) {
            if (!assessmentsStore) return [];

            return self.assessmentUsers
                .map(user => ({
                    ...user,
                    streakData: self.getUserStreakStatus(user.userId, assessmentsStore, companyId)
                }))
                .sort((a, b) => {
                    // Sort by current streak descending, then by longest streak descending
                    if (b.streakData.currentStreak !== a.streakData.currentStreak) {
                        return b.streakData.currentStreak - a.streakData.currentStreak;
                    }
                    return b.streakData.longestStreak - a.streakData.longestStreak;
                });
        },
    }));

export const UsersStore = types.compose(ApiStore, _usersStore)
    .actions(self => ({
        loadCurrentUser: flow(function* loadCurrentUser(userId) {
            yield self.getApi(`/users/${userId}`, self.currentUser);
        }),
        loadCompanyUsers: flow(function* loadCompanyUsers(companyId) {
            yield self.getApi(`/companies/${companyId}/users`, self.companyUsers);
        }),
        clearCurrentUser() {
            self.currentUser = getSnapshot(NullUser);
        },
        signUpUserFromSnapshot: flow(function* signUpUserFromSnapshot(userSnapshot) {
            self.isLoading = true;
            try {
                const body = { ...userSnapshot, active: true };
                delete body.userId;
                const model = clone(NullUser);
                body.email = body.email.toLowerCase();
                yield self.postApi('/users', body, model);
                self.companyUsers.push(model);
                self.isLoading = false;
            } catch (error) {
                throw error;
            } finally {
                self.isLoading = false;
            }
        }),
        deactivateCompanyAdmin: flow(function* deactivateCompanyAdmin(companyId) {
            self.isLoading = true;
            try {
                yield self.postApi('/users/companyexec/deactivate', { companyId });
                yield self.loadCompanyUsers(companyId);
            } catch (error) {
                throw error;
            } finally {
                self.isLoading = false;
            }
        }),
        getUsersForCompanies: flow(function* getUsersForCompanies(companyIds) {
            yield self.getApi(`/users/companies/${companyIds.join(',')}`, self.allUsers);
        }),
        lookupUserByEmail: flow(function* lookupUserByEmail(email) {
            try {
                return yield self.getApi(`/users/lookup-by-email?email=${encodeURIComponent(email)}`);
            } catch (error) {
                if (error.response?.status === 404) {
                    return null;
                }
                throw error;
            }
        }),
        attachExistingUserToCompany: flow(function* attachExistingUserToCompany(existing, companyId, preferredReportsToId) {
            const mergedCompanyIds = Array.from(new Set([...(existing.companyIds || []), companyId]));
            const patch = {
                firstName: existing.firstName,
                lastName: existing.lastName,
                userType: existing.userType,
                reportsToId: preferredReportsToId || existing.reportsToId,
                companyIds: mergedCompanyIds,
                isReadOnly: existing.isReadOnly,
                emailNotificationsEnabled: existing.emailNotificationsEnabled,
                active: true,
            };
            yield self.patchApi(`/users/${existing.userId}`, patch);
            yield self.loadCompanyUsers(companyId);
        })
    }));